import { deflateRaw, inflateRaw } from 'pako';

import { ActiveFilterOptions } from '~/components/filters/Filters';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import {
  FilterGroupKeys,
  FilterGroups,
  FilterState,
} from '~/hooks/use-filter-actions/useFilterActions';

import {
  CompressedFilterState,
  MinimizedFilter,
  MinimizedFilterOption,
} from './types';

// level: 9 (maximum compression)
const PAKO_DEFLATE_LEVEL = 9;
// memLevel: 9 (maximum memory, allows better compression patterns)
const PAKO_MEM_LEVEL = 9;
// windowBits: 15 (maximum window size, better for repetitive data)
const PAKO_WINDOW_BITS = 15;

export const minimizeFilterOptions = (
  activeFilterOptions: ActiveFilterOptions[]
): MinimizedFilter[] =>
  activeFilterOptions.map(filter => ({
    filter: filter.filter,
    filterOption: filter.options.map(option => {
      const value = option.value;
      const minimized: MinimizedFilterOption = { filterKey: option.key };

      // Always include value, but optimise common patterns
      if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>;

        // Only optimise the most common case: {id: key}
        if (Object.keys(obj).length === 1 && obj.id === option.key) {
          // omit filterValue
        } else {
          minimized.filterValue = value;
        }
      } else {
        minimized.filterValue = value;
      }

      // Preserve childName
      if (option.childName) {
        minimized.childFilter = option.childName;
      }

      // Preserve name to maintain computed display name
      if (option.name) {
        minimized.displayName = option.name;
      }

      return minimized;
    }),
  }));

export const reconstructFilterOptions = <
  T extends FilterGroupKeys = FilterGroupKeys
>(
  minimizedFilters: MinimizedFilter[],
  allFilters: FilterGroups<T>
): ActiveFilterOptions[] => {
  return minimizedFilters.map(minimizedFilter => {
    const expandedFilter = (Object.values(allFilters) as FilterState[])
      .flatMap(group => group.allFilters)
      .find(f => f.key === minimizedFilter.filter);

    const options = minimizedFilter.filterOption.map(minOption => {
      const fullOption = expandedFilter?.config?.options?.find(
        (opt: { key: string }) => opt.key === minOption.filterKey
      );
      // Prioritize stored name
      const name =
        minOption.displayName ?? fullOption?.name ?? minOption.filterKey;

      let value: Record<string, unknown>;

      if (minOption.filterValue === undefined) {
        // filterValue was omitted because it was just { id: filterKey }, restore it
        value = { id: minOption.filterKey };
      } else if (
        typeof minOption.filterValue === 'object' &&
        minOption.filterValue !== null
      ) {
        // filterValue exists, but we may have removed the id field, restore it if needed
        const filterValue = minOption.filterValue as Record<string, unknown>;
        value = { ...filterValue, id: minOption.filterKey };
      } else {
        // filterValue is a primitive, include both the key-value pair and id for consistency
        value = {
          [minOption.filterKey]: minOption.filterValue,
          id: minOption.filterKey,
        };
      }

      const option: FilterOption = {
        key: minOption.filterKey,
        name,
        value,
      };

      // Restore childName if it was present
      if (minOption.childFilter) {
        option.childName = minOption.childFilter;
      }

      return option;
    });

    return {
      id: minimizedFilter.filter,
      filter: minimizedFilter.filter,
      options,
    };
  });
};

const getShortKey = (key: string, index: number) => {
  const firstChar = key[0]?.toLowerCase() || '';
  return `${firstChar}${index}`;
};

export const decompressFilters = <T extends FilterGroupKeys = FilterGroupKeys>(
  compressedFilters: string,
  allFilters: FilterGroups<T>
): Partial<Record<T, ActiveFilterOptions[]>> | null => {
  try {
    // Decode from base64 URL-safe encoding
    let base64 = compressedFilters.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }

    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const decompressedBytes = inflateRaw(bytes, {
      windowBits: PAKO_WINDOW_BITS,
    });
    const decompressed = new TextDecoder().decode(decompressedBytes);

    if (!decompressed) {
      return null;
    }

    const state = JSON.parse(decompressed) as CompressedFilterState;
    const result: Partial<Record<T, ActiveFilterOptions[]>> = {};

    // Sort keys for deterministic ordering, then use index + first char
    const sortedGroupKeys = Object.keys(allFilters).sort() as T[];

    sortedGroupKeys.forEach((fullKey, index) => {
      const shortKey = getShortKey(fullKey, index);
      const isMinimized = state[shortKey];

      result[fullKey] = isMinimized
        ? reconstructFilterOptions(isMinimized, allFilters)
        : [];
    });

    return result;
  } catch (error) {
    console.error('Failed to decompress filters from query params:', error);
    return null;
  }
};

export const compressFilters = <T extends FilterGroupKeys = FilterGroupKeys>(
  groups: FilterGroups<T>
): string | undefined => {
  const compressedFilters: CompressedFilterState = {};

  // Sort keys for deterministic ordering, then use index + first char
  const sortedGroupKeys = Object.keys(groups).sort() as T[];

  sortedGroupKeys.forEach((fullKey, index) => {
    const group = groups[fullKey];

    if (group?.activeFilterOptions?.length > 0) {
      const shortKey = getShortKey(fullKey, index);
      compressedFilters[shortKey] = minimizeFilterOptions(
        group.activeFilterOptions
      );
    }
  });

  if (Object.keys(compressedFilters).length === 0) {
    return;
  }

  const json = JSON.stringify(compressedFilters);
  const compressed = deflateRaw(json, {
    level: PAKO_DEFLATE_LEVEL,
    windowBits: PAKO_WINDOW_BITS,
    memLevel: PAKO_MEM_LEVEL,
  });

  // Convert Uint8Array to base64 (handle large arrays)
  let binaryString = '';

  for (let i = 0; i < compressed.length; i++) {
    binaryString += String.fromCharCode(compressed[i]);
  }

  const base64 = btoa(binaryString);

  // Convert to URL-safe base64 format
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
